/**
 * InventoryPrecheckModal — Inventur-Vorbereitung (Jun-2026, User-Wunsch).
 *
 * Vor dem eigentlichen Zählen werden zwei Prüfungen abgearbeitet:
 *  1. Offene Aufträge mit Teilen (Hinweis — zuerst abschließen/abrechnen).
 *  2. Minusbestände (BLOCKER) — Liste mit „Bestand in Aufträgen" +
 *     „Kommissionsbestand" + Sammelkorrektur (alle Minus → 0 + Auftragsbestand).
 *
 * Der „Inventur starten"-Button bleibt deaktiviert, solange Minusbestände
 * existieren.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Loader2, Wrench, ScanLine, CheckCircle2, Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";

import api from "../../lib/api";
import { Button } from "../ui/button";
import ModalShell from "../../pages/wms/ModalShell";
import { fmtNum } from "../../pages/wms/wmsUtils";
import MobileInventoryQrModal from "../dealer/MobileInventoryQrModal";

export default function InventoryPrecheckModal({ onClose, onStart }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fixing, setFixing] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/wms/inventory/precheck");
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vorprüfung fehlgeschlagen");
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await api.get("/wms/inventory/precheck");
                if (active) setData(r.data);
            } catch (e) {
                toast.error(e.response?.data?.detail || "Vorprüfung fehlgeschlagen");
            }
            if (active) setLoading(false);
        })();
        return () => { active = false; };
    }, []);

    const fixNegative = async () => {
        if (!window.confirm("Alle Minusbestände auf 0 setzen und den in offenen Aufträgen gebundenen Bestand addieren?")) return;
        setFixing(true);
        try {
            const r = await api.post("/wms/inventory/fix-negative", {});
            toast.success(`${r.data.fixed} Minusbestand/Minusbestände korrigiert`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Korrektur fehlgeschlagen");
        }
        setFixing(false);
    };

    const openOrders = data?.open_orders_with_parts || [];
    const negative = data?.negative_stock || [];
    const canStart = !!data?.can_start;

    return (
        <ModalShell onClose={onClose} title="Inventur-Vorbereitung" wide testid="inventory-precheck-modal">
            {loading ? (
                <div className="py-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Prüfe Lager &amp; Aufträge…</div>
            ) : (
                <div className="space-y-5">
                    {/* 1) Offene Aufträge mit Teilen */}
                    <section data-testid="precheck-open-orders">
                        <div className="flex items-center gap-2 mb-2">
                            <Wrench className="w-4 h-4 text-amber-600" />
                            <h3 className="font-bold text-sm">Offene Aufträge mit Teilen</h3>
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-sm ${openOrders.length ? "bg-amber-100 text-amber-900" : "bg-green-100 text-green-800"}`}>
                                {openOrders.length}
                            </span>
                        </div>
                        {openOrders.length === 0 ? (
                            <div className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Keine offenen Aufträge mit verbauten Teilen.</div>
                        ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                                <p className="text-[11px] text-amber-900 mb-2">
                                    Diese Aufträge enthalten bereits Teile, sind aber noch nicht abgerechnet. Bitte zuerst abschließen/abrechnen, damit der Lagerabgang korrekt gebucht ist.
                                </p>
                                <div className="max-h-40 overflow-y-auto divide-y divide-amber-100">
                                    {openOrders.map((o) => (
                                        <div key={o.order_id} className="flex items-center justify-between py-1.5 text-xs" data-testid={`precheck-order-${o.order_id}`}>
                                            <div className="min-w-0">
                                                <span className="font-mono-num font-bold">{o.order_number}</span>
                                                <span className="ml-2 text-gray-600">{o.customer_name}</span>
                                                <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700">{o.status}</span>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-gray-600">{o.parts_count} Teil(e) · {fmtNum(o.parts_qty)} Stk</span>
                                                <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" onClick={() => navigate("/dealer/workshop-orders")} data-testid={`precheck-order-open-${o.order_id}`}>
                                                    Öffnen <ArrowRight className="w-3 h-3 ml-1" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>

                    {/* 2) Minusbestände (Blocker) */}
                    <section data-testid="precheck-negative">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                                <h3 className="font-bold text-sm">Minusbestände</h3>
                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-sm ${negative.length ? "bg-red-100 text-red-900" : "bg-green-100 text-green-800"}`}>
                                    {negative.length}
                                </span>
                            </div>
                            {negative.length > 0 && (
                                <Button size="sm" onClick={fixNegative} disabled={fixing} className="h-7 text-[11px] rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid="precheck-fix-negative">
                                    {fixing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                                    Alle korrigieren (0 + Auftragsbestand)
                                </Button>
                            )}
                        </div>
                        {negative.length === 0 ? (
                            <div className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Keine Minusbestände — Inventur kann starten.</div>
                        ) : (
                            <div className="border border-red-200 rounded-sm overflow-x-auto">
                                <table className="w-full text-xs min-w-[640px]">
                                    <thead className="bg-red-50 text-[10px] font-bold uppercase tracking-wider text-red-900">
                                        <tr>
                                            <th className="text-left px-3 py-2">Artikel</th>
                                            <th className="text-right px-3 py-2">Minusbestand</th>
                                            <th className="text-right px-3 py-2">in Aufträgen</th>
                                            <th className="text-right px-3 py-2">Kommission</th>
                                            <th className="text-right px-3 py-2">korrigiert auf</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {negative.map((a) => (
                                            <tr key={a.article_id} className="border-t border-red-100" data-testid={`precheck-neg-${a.article_id}`}>
                                                <td className="px-3 py-2">
                                                    <div className="font-mono-num text-[10px] text-gray-500">{a.sku}</div>
                                                    <div className="font-bold">{a.name}</div>
                                                    {a.is_consignment && <span className="text-[9px] uppercase tracking-wider text-purple-700 bg-purple-50 px-1 rounded-sm">Kommission</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono-num font-bold text-red-700">{fmtNum(a.stock_qty)}</td>
                                                <td className="px-3 py-2 text-right font-mono-num">{fmtNum(a.committed_in_orders)}</td>
                                                <td className="px-3 py-2 text-right font-mono-num text-purple-700">{a.is_consignment ? fmtNum(a.consignment_qty) : "—"}</td>
                                                <td className="px-3 py-2 text-right font-mono-num font-bold text-green-700">{fmtNum(a.suggested_qty)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    {/* 3) Handy-Inventur per QR — verlinkt auf den WMS-Barcode-Scanner */}
                    <section data-testid="precheck-mobile-qr" className="border border-gray-200 rounded-sm p-3 bg-gray-50">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-start gap-2">
                                <Smartphone className="w-4 h-4 text-[#111827] mt-0.5 shrink-0" />
                                <div>
                                    <h3 className="font-bold text-sm">Inventur per Handy</h3>
                                    <p className="text-[11px] text-gray-600">
                                        QR drucken/scannen — öffnet den Barcode-Scanner der Warenwirtschaft direkt am Handy (sofort eingeloggt).
                                    </p>
                                </div>
                            </div>
                            <Button size="sm" variant="outline" className="h-8 rounded-sm font-bold shrink-0"
                                    onClick={() => setQrOpen(true)} data-testid="precheck-mobile-qr-btn">
                                <Smartphone className="w-3.5 h-3.5 mr-1.5" /> Handy-Inventur (QR)
                            </Button>
                        </div>
                    </section>

                    {/* Aktionen */}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
                        <div className="text-[11px] text-gray-500">
                            {canStart ? "Alle Voraussetzungen erfüllt." : "Inventurstart blockiert, bis alle Minusbestände korrigiert sind."}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="precheck-cancel">Abbrechen</Button>
                            <Button
                                onClick={() => { onClose(); onStart(); }}
                                disabled={!canStart}
                                className="rounded-sm bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider disabled:opacity-50"
                                data-testid="precheck-start-btn"
                            >
                                <ScanLine className="w-4 h-4 mr-1.5" /> Inventur starten
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {qrOpen && (
                <MobileInventoryQrModal
                    onClose={() => setQrOpen(false)}
                    targetPath="/dealer/wms?action=scan-barcode"
                    title="Handy-Inventur (WMS-Scanner)"
                    subtitle="Mit der Handy-Kamera scannen — das Gerät ist sofort eingeloggt und öffnet direkt den Barcode-Scanner der Warenwirtschaft."
                />
            )}
        </ModalShell>
    );
}
